import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import Dropdown, { type DropdownOption } from "@/components/base/Dropdown";
import { movies } from "@/mocks/movies";
import { searchMovies, type CategoryKey } from "@/utils/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { Movie } from "@/mocks/movies";

const sortOptions: DropdownOption[] = [
  { value: "latest", label: "按最新更新" },
  { value: "hot", label: "按播放热度" },
  { value: "rating", label: "按评分最高" },
];

const typeFilters: { value: string; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "tv", label: "电视剧" },
  { value: "movie", label: "电影" },
  { value: "variety", label: "综艺" },
];

const hotMovies: Movie[] = [...movies].sort((a, b) => b.views - a.views).slice(0, 6);

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  const [keyword, setKeyword] = useState(q);
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState<"latest" | "hot" | "rating">("latest");

  useDocumentMeta(
    q ? `搜索“${q}” - 爱看视频` : "搜索影视 - 爱看视频",
    q
      ? `在爱看视频搜索“${q}”，查看相关的电视剧、电影和综艺结果。`
      : "爱看视频搜索页面，快速查找电视剧、电影和综艺内容。"
  );

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useEffect(() => {
    setTypeFilter("all");
    setSort("latest");
  }, [q]);

  const results = useMemo(() => {
    const base = searchMovies(q);
    const filtered = base.filter((movie) =>
      typeFilter === "all" ? true : movie.type === (typeFilter as CategoryKey)
    );
    return filtered.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [q, typeFilter, sort]);

  const handleChange = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setParams({ q: value }, { replace: true });
    } else {
      setParams({}, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <div className="rounded-lg bg-background-100/60 border border-background-200 p-5 md:p-6">
            <h1 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
              搜索影视
            </h1>
            <div className="relative mt-4 max-w-2xl">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => handleChange(event.target.value)}
                placeholder="输入关键词，如剧名、演员、类型"
                className="w-full h-12 pl-11 pr-4 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {typeFilters.map((item) => {
                const active = item.value === typeFilter;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setTypeFilter(item.value)}
                    className={`px-3.5 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50 font-medium"
                        : "bg-background-50 text-foreground-700 border border-background-200 hover:text-primary-600 hover:border-primary-300"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!q.trim() ? (
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="支持按剧名、演员、类型等进行模糊匹配，快速找到想看的影视"
            />
          ) : (
            <>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  关键词“<span className="text-primary-600 font-medium">{q}</span>” 共找到{" "}
                  <span className="text-primary-600 font-medium">{results.length}</span> 个结果
                </p>
                <Dropdown value={sort} options={sortOptions} onChange={(v) => setSort(v as "latest" | "hot" | "rating")} />
              </div>

              {results.length === 0 ? (
                <>
                  <EmptyState
                    icon="ri-emotion-sad-line"
                    title="未找到相关内容"
                    description="换个关键词试试，或者看看下面的热门影视推荐"
                  />
                  <div className="pb-4">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                      <h2 className="font-heading text-xl font-black text-foreground-950">
                        热门影视推荐
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                      {hotMovies.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} className="animate-fade-up" />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-10">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} className="animate-fade-up" />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}